import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import type { ApiError } from "@/api/errors";
import type { components } from "@/api/schema";

// Types come from the pulled BE contract (openapi.yaml → schema.d.ts) — the
// push-state / push-log / resume endpoints are part of it now, so the earlier
// hand-typed shapes and the private fetch wrapper are gone (review C1).
export type PushState = components["schemas"]["StockPushState"];
export type PushLogEntry = components["schemas"]["StockPushLogEntry"];
export type Marketplace = PushState["marketplace"];
export type PushMode = PushState["mode"];

export const pushKeys = {
  states: ["stock", "push-state"] as const,
  log: (sku: string, marketplace: string) => ["stock", "push-log", sku, marketplace] as const,
};

/** One row per marketplace's current push mode (+ breaker-pause state, when tripped). */
export function usePushStates() {
  return useQuery({
    queryKey: pushKeys.states,
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/stock/push-state");
      if (error) throw error;
      return data;
    },
    staleTime: 30_000,
  });
}

/** A row's push log, newest first — feeds the Push history drawer. */
export function usePushLog(sku: string, marketplace: string, enabled = true) {
  return useQuery({
    queryKey: pushKeys.log(sku, marketplace),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/stock/{sku}/{mkp}/push-log", {
        params: { path: { sku, mkp: marketplace as Marketplace } },
      });
      if (error) throw error;
      return data;
    },
    enabled: enabled && !!sku && !!marketplace,
  });
}

/** Resumes a marketplace's push after the breaker paused it. */
export function useResumePush() {
  const queryClient = useQueryClient();
  return useMutation<PushState, ApiError, { marketplace: Marketplace }>({
    meta: { silentError: true },
    mutationFn: async ({ marketplace }) => {
      const { data, error } = await apiClient.POST("/v1/stock/push-state/{mkp}/resume", {
        params: { path: { mkp: marketplace } },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: pushKeys.states }),
  });
}
